import { memo } from "react";
import { useLingui } from "@lingui/react";
import { ScrollableRow } from "src/atoms/ScrollableRow";
import { useListingStore } from "src/hooks/stores/use-listing-store";
import type { ListingFacetKeys } from "src/types/adapters/listing.type";
import { BrandFilter, Filter, Range } from "src/molecules/Filter";
import { Sizes } from "src/molecules/Filter/components/Sizes";
import { useFeatureFlags } from "src/hooks/useFeatureFlags";
import { useFacetContext } from "../Facets/facet.context";
import { FACET_LABELS } from "src/utils/search-provider/constants";

const SINGLE_COLUMN_FACETS: readonly ListingFacetKeys[] = [
  "brands",
  "discount",
] as const;

const SINGLE_SELECT_FACETS: readonly ListingFacetKeys[] = ["discount"] as const;

const EXCLUDED_DESKTOP_FACETS: readonly ListingFacetKeys[] = [
  "category",
] as const;

function Filters(): JSX.Element {
  const { selectedFacets } = useFacetContext();

  return (
    <ScrollableRow>
      {selectedFacets
        .filter((key) => !EXCLUDED_DESKTOP_FACETS.includes(key))
        .map((key) =>
          key === "size" ? (
            <SizeFacetSlot key={key} />
          ) : (
            <FacetSlot key={key} facetKey={key} />
          )
        )}
    </ScrollableRow>
  );
}

const FacetSlot = memo(function FacetSlot({
  facetKey,
}: {
  facetKey: ListingFacetKeys;
}): JSX.Element | null {
  const { _ } = useLingui();
  const facet = useListingStore((state) => state.facets?.get(facetKey));

  if (!facet) {
    return null;
  }

  const placeholder = _(FACET_LABELS[facet.key]);

  if (facet.key === "brands") {
    return <BrandFilter placeholder={placeholder} facet={facet} />;
  }

  if (facet.key === "price") {
    return <Range facet={facet} placeholder={placeholder} />;
  }

  if (facet.key === "colors") {
    return (
      <Filter
        placeholder={placeholder}
        type="color"
        facet={facet}
        isMultiColumn
      />
    );
  }

  return (
    <Filter
      placeholder={placeholder}
      type={
        SINGLE_SELECT_FACETS.includes(facet.key) ? "select" : "multi-select"
      }
      facet={facet}
      isMultiColumn={!SINGLE_COLUMN_FACETS.includes(facet.key)}
    />
  );
});

const SizeFacetSlot = memo(function SizeFacetSlot(): JSX.Element | null {
  const { _ } = useLingui();
  const { sizingFiltersWithSchema } = useFeatureFlags();
  const facet = useListingStore((state) => state.facets?.get("size"));

  if (!facet) {
    return null;
  }

  const placeholder = _(FACET_LABELS[facet.key]);

  if (sizingFiltersWithSchema) {
    return <Sizes facet={facet} placeholder={placeholder} />;
  }

  return (
    <Filter
      placeholder={placeholder}
      type="multi-select"
      facet={facet}
      isMultiColumn
    />
  );
});

export { Filters };
